/**
 * ImporterCSV function allows users to import a CSV file, parse its content,
 * and display the parsed data in a table format. It uses the react-dropzone
 * library for file drop functionality and PapaParse for CSV parsing.
 *
 * @function
 * @param {Object} props - The function props.
 * @param {Function} props.setFile - Function to set the file content.
 *
 * @example
 * <ImporterCSV setFile={setFile} />
 *
 * @returns {JSX.Element} The rendered function.
 *
 * @remarks
 * - The function checks if the CSV file contains a column named "scientificName".
 * - If the column is not found, an error message is displayed.
 * - If the column is found, the CSV content is parsed and displayed in a table.
 * - The function also checks for semicolons in the scientific names and displays an error if any are found.
 */

import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { StoreFile } from '../../hooks/store';
import { fillArray, TableCSV } from './Table';
import * as Papa from 'papaparse';
import CsvFileDropzone from '@front-ends/common/src/components/app/CsvFileDropzone';
import { useTranslation } from 'react-i18next';

export default function ImporterCSV({ setFile, setImportTime }: any) {

  const { t } = useTranslation();

  const [errorHeader, setErrorHeader] = useState(false);
  const [errorParsing, setErrorParsing] = useState(false);
  const [indexError, setIndexError] = useState(0);
  const [table, setTable] = useState(false);

  function fileOnLoadParameters(event: any) {
    const text = event.target?.result as string;

    let regexHeader = /scientificName/g;
    let matchHeader = text.match(regexHeader);
    matchHeader === null ? setErrorHeader(true) : setErrorHeader(false);
    if (text) {
      setErrorParsing(false);
      csvFileToArray(text);
      setTable(true);
    } else {
      setTable(false);
    }
  }
  
  const csvFileToArray = (string: string) : Array<{ scientificName: string }> => {
    
    const parseResult = Papa.parse(string, {
      header: true,
      delimiter: ';',
      quoteChar: '\'',
      skipEmptyLines: true
    });
  
    if (parseResult.errors.length > 0) {
      console.error('Erreurs de parsing:', parseResult.errors);
    }

    const arrayTable = parseResult.data.filter((value: any, index: number, self: any) =>
      index === self.findIndex((t: any) => (
        t.scientificName === value.scientificName
      ))
    ).map((row: any, index: number) => ({
      id: index + 1,
      scientificName: row.scientificName
    }));

    const csvString = [
      [
        "scientificName"
      ],
      ...arrayTable.map((i :any) => [
        i.scientificName,
      ])
    ].join("\n");

    let regexSemicoma = /;/g;
    const lines = csvString.split('\n');
    lines.map((line, index) => {
      index = index + 1;
      if (regexSemicoma.test(line)) {
        setErrorParsing(true);
        setIndexError(index);
      }
    });

    setFile(csvString);
    fillArray(arrayTable);
    StoreFile.setState({isFinished: true});
    return arrayTable
  };

  return (
    <div className="container">
      <CsvFileDropzone
        setFile={setFile}
        fileOnLoadParameters={fileOnLoadParameters}
        label={t("import-context")}
      />
      {errorHeader && (
        <Typography sx={{ mt: 2, mb: 1, color: "red" }}>
          {t("file-reject-error-1")}
        </Typography>
      )}
      {errorParsing && (
        <Typography sx={{ mt: 2, mb: 1, color: "red" }}>
          {t("file-reject-error-2-1")} {indexError} {t("file-reject-error-2-2")}
        </Typography>
      )}
      {table && !errorHeader && !errorParsing && (
        <TableCSV setImportTime={setImportTime} />
      )}
    </div>
  );
}